import { LitElement, html, css } from 'lit';
import { api } from '../api.js';
import { labPresentation, labStatusDot, SELECTABLE_LABS } from '../labs.js';
import { auth } from '../auth.js';
import { BETA_FEATURES } from '@mv/models';
import { Building2, Settings2, Plus, AlertCircle, Copy, Check, Users, Building, Zap, ZapOff, Search, Pencil, Trash2, KeyRound, RefreshCw, ChevronDown, ChevronUp, X, ListChecks } from 'lucide';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { live } from 'lit/directives/live.js';
import { repeat } from 'lit/directives/repeat.js';

import { lucideIcon } from '../icon.js';
import { confirmDialog, alertDialog } from '../utils/confirm-dialog.js';
import { formatDate as formatDateNL, formatTime, parseTimestamp } from '../utils/datetime.js';

/**
 * De functionaliteiten in de AI-kosten, met hun kleur in de grafiek; ids zoals de backend ze
 * teruggeeft. Rapportage gebruikt nog geen AI en staat grijs in de tabel, niet in de grafiek.
 */
const AI_FUNCTIES = [
    { id: 'boorprofielen', label: 'Boorprofielen', uitleg: 'Spraakmemo’s verwerken: transcriptie en extractie', kleur: '#4b9d35' },
    { id: 'vooronderzoek', label: 'Vooronderzoek', uitleg: 'Veldwerkopdracht inlezen', kleur: 'oklch(0.62 0.12 245)' },
    { id: 'labopdracht', label: 'Labopdracht', uitleg: 'Flip', kleur: 'oklch(0.72 0.13 70)' },
    { id: 'rapportage', label: 'Rapportage', uitleg: 'Gebruikt nog geen AI', kleur: '#DDE1E5', geen: true },
];

/** De periodes boven de grafiek; `weken` null = vanaf de eerste aanroep. */
const AI_BEREIKEN = [
    { id: '4w', label: '4 weken', weken: 4 },
    { id: '3m', label: '3 maanden', weken: 13 },
    { id: 'alles', label: 'Alles', weken: null },
];

const fmtDollar = (n, d = 2) => `$${(n ?? 0).toLocaleString('nl-NL', { minimumFractionDigits: d, maximumFractionDigits: d })}`;
const aandeelLabel = (a) => (a === 0 ? '—' : a < 0.01 ? '<1%' : `${Math.round(a * 100)}%`);

/**
 * Dagbedragen → weken (maandag t/m zondag, UTC), per functionaliteit. Lege weken tellen mee als
 * 0, anders lijkt een stille week niet te bestaan. Bij 'Alles' vanaf de week van de eerste
 * aanroep, anders de laatste `weken` weken tot en met deze week.
 */
function aiWeken(dagen, weken) {
    const datum = (s) => new Date(`${s}T00:00:00Z`);
    const maandag = (d) => { const x = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())); x.setUTCDate(x.getUTCDate() - ((x.getUTCDay() + 6) % 7)); return x; };
    const nu = maandag(new Date());
    const begin = weken ? new Date(nu.getTime() - (weken - 1) * 7 * 86400000)
        : (dagen.length ? maandag(datum(dagen[0].dag)) : nu);
    const lijst = [];
    const index = new Map();
    for (let d = begin; d <= nu; d = new Date(d.getTime() + 7 * 86400000)) {
        const sleutel = d.toISOString().slice(0, 10);
        index.set(sleutel, lijst.length);
        lijst.push({ sleutel, kort: d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', timeZone: 'UTC' }), kosten: {}, aanroepen: {} });
    }
    for (const r of dagen) {
        const i = index.get(maandag(datum(r.dag)).toISOString().slice(0, 10));
        if (i == null) continue;
        const w = lijst[i];
        w.kosten[r.categorie] = (w.kosten[r.categorie] || 0) + r.kosten;
        w.aanroepen[r.categorie] = (w.aanroepen[r.categorie] || 0) + r.aanroepen;
    }
    return lijst;
}

const somWeek = (w, veld) => Object.values(w[veld]).reduce((a, b) => a + b, 0);

import { unsafeCSS } from 'lit';
import base from '../styles/base.css?inline';
import styles from '../styles/company-dashboard.css?inline';

/**
 * Terugval voor het geval de Worker de capaciteit nog niet kan opgeven.
 *
 * De echte lijst komt van `GET /api/companies/bindings`, want alleen de Worker weet welke
 * databases er daadwerkelijk aan hem hangen. Deze lijst is er voor het gat tussen twee
 * deploys: de backoffice staat er soms eerder op dan de Worker. Zet iemand er databases bij,
 * dan hoeft hier niets bij — dat is precies waarom het niet meer vanaf deze lijst werkt.
 */
const AVAILABLE_BINDINGS = [
    'DB_COMPANY_0',
    'DB_COMPANY_1',
    'DB_COMPANY_2',
    'DB_COMPANY_3',
    'DB_COMPANY_4',
    'DB_COMPANY_5',
    'DB_COMPANY_6',
    'DB_COMPANY_7',
    'DB_COMPANY_8',
    'DB_COMPANY_9',
    'DB_COMPANY_10',
];

export class CompanyDashboard extends LitElement {
    static properties = {
        companies: { type: Array },
        filteredCompanies: { type: Array },
        searchQuery: { type: String },
        isLoading: { type: Boolean },
        error: { type: String },

        // Modal state
        isCreateModalOpen: { type: Boolean },
        newCompanyName: { type: String },
        newAdminEmail: { type: String },
        newAdminFirstName: { type: String },
        newAdminLastName: { type: String },
        newDbBinding: { type: String },
        newLicense: { type: String },
        isCreating: { type: Boolean },

        // Edit state
        isEditModalOpen: { type: Boolean },
        editTarget: { type: Object },
        editName: { type: String },
        editDbBinding: { type: String },
        isSaving: { type: Boolean },

        // Result state
        createdResult: { type: Object },

        // Databasecapaciteit zoals de Worker hem kent (null = nog niet opgehaald)
        capacity: { type: Object },
        // Was het vol toen dit venster openging? Zie openCreateModal.
        noCapacity: { type: Boolean },

        // Detail popup (name + DB binding edit + members)
        detailCompany: { type: Object },
        editingField: { type: String },
        editValue: { type: String },
        busy: { type: Boolean },
        members: { type: Array },
        membersError: { type: String },
        labConnections: { type: Array },
        labConnectionsError: { type: String },
        labFormOpen: { type: Boolean },
        labForm: { type: Object },
        labBusy: { type: Boolean },
        _labCredentialForms: { type: Object, state: true },
        // Koppelingen van ALLE bedrijven, voor de tegels in de lijst.
        allLabConnections: { type: Array },
        detailTab: { type: String },
        aiTotalen: { type: Object },     // bedrijf-id → { totaal, aanroepen, onbekend }
        aiDagen: { type: Array },        // AI-kosten van het geopende bedrijf, per dag en functionaliteit
        aiFout: { type: String },
        aiBereik: { type: String },      // '4w' | '3m' | 'alles'
        aiHover: { type: Number },       // week onder de muis in de grafiek, of null
        expandedLab: { type: String },
    };

    constructor() {
        super();
        this.companies = [];
        this.filteredCompanies = [];
        this.searchQuery = '';
        this.isLoading = true;
        this.error = null;
        this.isCreateModalOpen = false;
        this.newCompanyName = '';
        this.newAdminEmail = '';
        // Elke keer opnieuw op de basislicentie: blijft de vorige keuze staan, dan deelt de
        // volgende aanmaak ongemerkt GeoDesk uit.
        this.newLicense = 'geovoice';
        this.newAdminFirstName = '';
        this.newAdminLastName = '';
        this.newDbBinding = '';
        this.isCreating = false;
        this.createdResult = null;
        this.isEditModalOpen = false;
        this.editTarget = null;
        this.editName = '';
        this.editDbBinding = '';
        this.isSaving = false;
        this.detailCompany = null;
        this.editingField = null;
        this.editValue = '';
        this.capacity = null;
        this.noCapacity = false;
        this.busy = false;
        this.members = null;
        this.membersError = null;
        this.labConnections = [];
        this.labConnectionsError = null;
        this.labFormOpen = false;
        this.labForm = { labKey: 'sgs', environment: 'test', username: '', password: '' };
        this.labBusy = false;
        this._labCredentialForms = {};
        this.allLabConnections = [];
        this.detailTab = 'company';
        this.aiTotalen = {};
        this.aiDagen = null;
        this.aiFout = '';
        this.aiBereik = 'alles';
        this.aiHover = null;
        this.expandedLab = null;
    }

    connectedCallback() {
        super.connectedCallback();
        this.checkAccess();
        this.loadCompanies();
        this._onEsc = (e) => {
            if (e.key !== 'Escape') return;
            if (this.detailCompany) this.closeDetail();
            else if (this.isCreateModalOpen) this.closeCreateModal();
        };
        document.addEventListener('keydown', this._onEsc);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('keydown', this._onEsc);
    }

    checkAccess() {
        // Sysadmin-only (user-level flag; company-independent).
        if (!auth.isSystemAdmin()) {
            window.location.href = '/';
        }
    }

    /** Open the company detail popup (name + DB binding edit + members). */
    async openDetail(row) {
        const company = this.companies.find(c => String(c.id) === String(row.id)) || row;
        // Het hele bedrijf overnemen, en niet een handvol velden opsommen.
        //
        // Die opsomming was de oorzaak van een stille fout: `license` en `sysadmin_access`
        // stonden er niet in, dus toonde de popup voor beide de standaardwaarde -- een bedrijf
        // mét GeoDesk las als GeoVoice, en een aangezette schakelaar stond bij het heropenen
        // weer uit. Er ging niets verloren in de database; je zag alleen iets anders dan er
        // stond. Elk veld dat er later bij komt heeft nu hetzelfde probleem niet.
        this.detailCompany = { ...company };
        this.editingField = null;
        this.editValue = '';
        this.members = null;
        this.membersError = null;
        this.labConnections = [];
        this.labConnectionsError = null;
        this.labFormOpen = false;
        this._labCredentialForms = {};
        this.detailTab = 'company';
        this.aiDagen = null;
        this.aiFout = '';
        this.aiBereik = 'alles';
        this.aiHover = null;
        // Apart en zonder te wachten: de grafiek is pas nodig als je het tabblad opent.
        api.getAdminCompanyAiCosts(company.id)
            .then((r) => { if (this.detailCompany?.id === company.id) this.aiDagen = r?.dagen || []; })
            .catch((e) => { if (this.detailCompany?.id === company.id) { this.aiDagen = []; this.aiFout = e.message || 'Kon de AI-kosten niet laden'; } });
        try {
            const [members, labs] = await Promise.all([
                api.getCompanyMembers(company.id),
                api.getAdminCompanyLabConnections(company.id).catch((e) => ({ _error: e.message })),
            ]);
            this.members = members;
            if (labs?._error) {
                this.labConnectionsError = labs._error;
                this.labConnections = [];
            } else {
                this.labConnections = labs?.labConnections || [];
            }
        } catch (e) {
            this.membersError = e.message || 'Kon leden niet laden';
            this.members = [];
        }
    }

    closeDetail() {
        this.detailCompany = null;
        this.editingField = null;
        this.members = null;
        this.membersError = null;
        this.labConnections = [];
        this.labConnectionsError = null;
        this.labFormOpen = false;
        this._labCredentialForms = {};
    }

    startEdit(field) {
        this.editingField = field;
        this.editValue = field === 'name' ? this.detailCompany.name : this.detailCompany.db_binding;
    }
    cancelEdit() { this.editingField = null; this.editValue = ''; }

    async saveName() {
        const name = (this.editValue || '').trim();
        if (!name) return;
        this.busy = true;
        try {
            await api.updateCompany(this.detailCompany.id, { name });
            this.detailCompany = { ...this.detailCompany, name };
            this.editingField = null;
            await this.loadCompanies();
        } catch (e) { alertDialog({ message: `Opslaan mislukt: ${e.message}` }); }
        finally { this.busy = false; }
    }

    async saveBinding() {
        const db_binding = (this.editValue || '').trim();
        if (!db_binding || db_binding === this.detailCompany.db_binding) { this.editingField = null; return; }
        // Double-check: repointing the DB binding switches the company to a different database.
        const ok = await confirmDialog({
            title: 'DB Binding wijzigen?',
            message: `Je staat op het punt de DB Binding van "${this.detailCompany.name}" te wijzigen naar ${db_binding}. Hiermee koppel je dit bedrijf aan een andere database — alle projecten/opnames van dit bedrijf komen dan uit die andere database. Doe dit alleen als je precies weet wat je doet.`,
            confirmLabel: 'Ja, binding wijzigen',
            danger: true,
        });
        if (!ok) return;
        this.busy = true;
        try {
            await api.updateCompany(this.detailCompany.id, { db_binding });
            this.detailCompany = { ...this.detailCompany, db_binding };
            this.editingField = null;
            await this.loadCompanies();
        } catch (e) { alertDialog({ message: `Opslaan mislukt: ${e.message}` }); }
        finally { this.busy = false; }
    }

    /**
     * Licentie omzetten. Geen bevestiging: dit is met één tik terug te draaien, en het raakt
     * geen data — alleen of de labkoppeling zichtbaar is voor dat bedrijf.
     */
    async saveLicense(license) {
        if (!license || license === (this.detailCompany.license || 'geovoice')) return;
        this.busy = true;
        try {
            await api.updateCompany(this.detailCompany.id, { license });
            this.detailCompany = { ...this.detailCompany, license };
            await this.loadCompanies();
        } catch (e) { alertDialog({ message: `Opslaan mislukt: ${e.message}` }); }
        finally { this.busy = false; }
    }

    /**
     * Meekijken door systeembeheerders aan- of uitzetten voor dit bedrijf.
     *
     * Dit verving de knop "Voeg mij toe". Die maakte een echt LIDMAATSCHAP aan: volledige
     * schrijfrechten, en het bedrijf zag een vreemde tussen zijn eigen mensen staan op de
     * Team-pagina. Meekijken geeft uitsluitend leesrechten en laat geen spoor in de ledenlijst
     * achter — het bedrijf kan dezelfde schakelaar zelf omzetten.
     */
    async saveSysadminAccess(enabled) {
        this.busy = true;
        try {
            await api.updateCompany(this.detailCompany.id, { sysadmin_access: enabled });
            this.detailCompany = { ...this.detailCompany, sysadmin_access: enabled ? 1 : 0 };
            await this.loadCompanies();
            // De bedrijvenkiezer linksboven toont de meekijk-bedrijven; die lijst verandert hier.
            await auth.refreshCompanies();
        } catch (e) { alertDialog({ message: `Opslaan mislukt: ${e.message}` }); }
        finally { this.busy = false; }
    }

    /** De bètafuncties van een bedrijf opslaan (de hele lijst). */
    async saveBetaFeatures(company, lijst) {
        const nieuw = [...new Set(lijst)];
        this.busy = true;
        try {
            await api.updateCompany(company.id, { beta_features: nieuw });
            if (this.detailCompany && String(this.detailCompany.id) === String(company.id)) {
                this.detailCompany = { ...this.detailCompany, beta_features: nieuw };
            }
            await this.loadCompanies();
        } catch (e) { alertDialog({ message: `Opslaan mislukt: ${e.message}` }); }
        finally { this.busy = false; }
    }

    /** De aangezette bètafuncties als tegels, voor de kolom in de lijst. */
    _renderBetaTegels(c) {
        const aan = BETA_FEATURES.filter(f => (c.beta_features || []).includes(f.id));
        if (!aan.length) return html`<span style="color:var(--mv-text-sub,#6c757d);">—</span>`;
        return html`<span style="display:flex; flex-wrap:wrap; gap:6px;">${aan.map(f => html`
          <span class="status-pill" style="background:var(--mv-primary-tint,#e9f5e6); color:var(--mv-primary);">${f.label}</span>`)}</span>`;
    }

    /**
     * Bètafuncties als één invoerveld: de aangezette functies staan als tegels in de balk (met ×
     * om ze weg te halen), de rest kies je uit de keuzelijst achteraan.
     */
    _renderBetaInvoer(c) {
        const gekozen = c.beta_features || [];
        const over = BETA_FEATURES.filter(f => !gekozen.includes(f.id));
        const label = (id) => BETA_FEATURES.find(f => f.id === id)?.label || id;
        return html`
          <div style="display:flex; flex-wrap:wrap; align-items:center; gap:6px; min-height:40px; padding:5px 8px;
                      border:1px solid var(--mv-border); border-radius:8px; background:var(--mv-surface,#fff); opacity:${this.busy ? '0.6' : '1'};">
            ${gekozen.map(id => html`
              <span style="display:inline-flex; align-items:center; gap:4px; padding:3px 4px 3px 10px; border-radius:6px;
                           background:var(--mv-primary-tint,#e9f5e6); color:var(--mv-primary); font-weight:600; font-size:var(--mv-text-sm);">
                ${label(id)}
                <button type="button" title="Weghalen" ?disabled=${this.busy}
                  @click=${() => this.saveBetaFeatures(c, gekozen.filter(x => x !== id))}
                  style="border:none; background:none; color:inherit; cursor:pointer; font-size:15px; line-height:1; padding:0 4px;">×</button>
              </span>`)}
            ${over.length ? html`
              <select ?disabled=${this.busy} aria-label="Bètafunctie toevoegen"
                @change=${(e) => { const v = e.target.value; e.target.value = ''; if (v) this.saveBetaFeatures(c, [...gekozen, v]); }}
                style="flex:1; min-width:140px; border:none; background:transparent; font:inherit; color:var(--mv-text-sub,#6c757d); cursor:pointer; outline:none;">
                <option value="">${gekozen.length ? 'Toevoegen…' : 'Kies bètafuncties…'}</option>
                ${over.map(f => html`<option value=${f.id}>${f.label}</option>`)}
              </select>` : ''}
          </div>`;
    }

    /**
     * Een schakelaar. Inline gestyled, net als de rest van dit scherm: de Lit-stijlen van deze
     * backoffice komen uit een `?inline`-CSS-bestand en wijzigingen daaraan komen niet door via
     * Vite tijdens een lopende sessie.
     */
    _toggle(checked, onChange) {
        return html`
          <span role="switch" aria-checked=${checked ? 'true' : 'false'}
            @click=${() => { if (!this.busy) onChange(!checked); }}
            style="flex:none; width:44px; height:24px; border-radius:999px; position:relative;
                   cursor:${this.busy ? 'default' : 'pointer'}; opacity:${this.busy ? '0.6' : '1'};
                   background:${checked ? 'var(--mv-primary)' : 'var(--mv-border)'}; transition:background .15s;">
            <span style="position:absolute; top:2px; left:${checked ? '22px' : '2px'}; width:20px; height:20px;
                         border-radius:50%; background:#fff; transition:left .15s;
                         box-shadow:0 1px 3px rgba(0,0,0,.25);"></span>
          </span>`;
    }

    _roleLabel(role) {
        return role === 'owner' ? 'Eigenaar' : role === 'manager' ? 'Beheerder' : role === 'guest' ? 'Gast' : 'Lid';
    }

    /** Change a member's role in this company (Beheerder/Lid), with confirmation. */
    async changeMemberRole(m, role) {
        if (role === m.role) return;
        const ok = await confirmDialog({
            title: 'Rol wijzigen?',
            message: `Je wijzigt de rol van ${m.email} bij ${this.detailCompany.name} naar ${this._roleLabel(role)}. Dit bepaalt wat deze gebruiker binnen dit bedrijf mag.`,
            confirmLabel: 'Ja, rol wijzigen',
        });
        if (!ok) { this.members = [...(this.members || [])]; return; } // reset the select
        this.busy = true;
        try {
            await api.setUserMembershipRole(m.id, this.detailCompany.id, role);
            this.members = await api.getCompanyMembers(this.detailCompany.id);
        } catch (e) {
            alertDialog({ message: `Rol wijzigen mislukt: ${e.message}` });
            this.members = [...(this.members || [])];
        } finally { this.busy = false; }
    }

    /** Remove a member from this company (revoke access), with confirmation. */
    async removeMember(m) {
        const ok = await confirmDialog({
            title: 'Lid verwijderen?',
            message: `${m.email} verliest direct toegang tot ${this.detailCompany.name} en alle bijbehorende projecten. Dit kan niet ongedaan worden gemaakt — je kunt ze later opnieuw uitnodigen.`,
            confirmLabel: 'Ja, verwijderen',
            danger: true,
        });
        if (!ok) return;
        this.busy = true;
        try {
            await api.removeUserMembership(m.id, this.detailCompany.id);
            this.members = await api.getCompanyMembers(this.detailCompany.id);
        } catch (e) {
            alertDialog({ message: `Verwijderen mislukt: ${e.message}` });
        } finally { this.busy = false; }
    }

    async loadCompanies() {
        try {
            this.isLoading = true;
            const data = await api.getCompanies();
            this.companies = data;
            this.filterCompanies();
            // Capaciteit apart: mislukt hij, dan is de bedrijvenlijst nog steeds bruikbaar.
            // Een oudere Worker kent deze route nog niet — dan valt alles terug op de lijst
            // in dit bestand, zoals het hiervoor werkte.
            try { this.capacity = await api.getCompanyBindings(); }
            catch (_) { this.capacity = null; }
            // Labkoppelingen apart: mislukt dit, dan blijft de lijst gewoon bruikbaar en
            // staat er enkel geen tegel bij. Een oudere Worker kent deze route nog niet.
            try { this.allLabConnections = (await api.getAdminLabConnections())?.labConnections || []; }
            catch (_) { this.allLabConnections = []; }
            // AI-kosten apart: duurt het of mislukt het, dan staat de lijst er al.
            api.getAdminAiCosts()
                .then((r) => { this.aiTotalen = Object.fromEntries((r?.companies || []).map((c) => [c.id, c])); })
                .catch(() => { this.aiTotalen = {}; });
        } catch (error) {
            console.error('Error loading companies:', error);
            this.error = 'Kon bedrijven niet laden. Probeer het later opnieuw.';
        } finally {
            this.isLoading = false;
        }
    }

    /** Koppelingen van één bedrijf, uit de lijst die bij het laden is opgehaald. */
    _labsForCompany(companyId) {
        return (this.allLabConnections || []).filter(c => String(c.companyId) === String(companyId));
    }

    /** Kleine tegel: logo, naam en een stip die de status vertelt. */
    _renderLabChip(conn) {
        const lab = labPresentation(conn.labKey);
        const dot = labStatusDot(conn);
        return html`
          <span title=${`${lab.name} — ${dot.label}`}
            style="display:inline-flex; align-items:center; gap:6px; padding:5px 9px 5px 6px; border:1px solid var(--mv-border); border-radius:var(--mv-radius-sm); background:var(--mv-surface);">
            ${this._renderLabMark(lab, 18)}
            <span style="font-size:var(--mv-text-sm); font-weight:var(--mv-weight-bold); color:var(--mv-text); white-space:nowrap;">${lab.name}</span>
            <span class="mv-dot" style="background:${dot.color};"></span>
          </span>`;
    }

    /**
     * Het logovierkantje. Ontbreekt het logo, dan vallen we terug op de initialen, zodat een
     * lab dat we nog niet kennen toch een herkenbare tegel krijgt.
     */
    _renderLabMark(lab, size) {
        // Straal schaalt mee: een vierkantje van 18px met dezelfde straal als een kaart van
        // 44px oogt bol. Onder de 24px de kleinste trap, daarboven de volgende.
        const radius = size <= 24 ? 'var(--mv-radius-xs)' : 'var(--mv-radius-sm)';
        const box = `width:${size}px; height:${size}px; border-radius:${radius}; flex:none;`;
        if (!lab.logo) {
            return html`<span style="${box} background:${lab.accent}; color:#fff; font-size:${Math.round(size * 0.4)}px; font-weight:700; display:flex; align-items:center; justify-content:center;">${lab.name.slice(0, 2).toUpperCase()}</span>`;
        }
        return html`<span style="${box} background:#fff; border:1px solid var(--mv-border); display:flex; align-items:center; justify-content:center; overflow:hidden;">
            <img src=${lab.logo} alt="" style="max-width:82%; max-height:82%; object-fit:contain;">
          </span>`;
    }

    _companyInitials(name) {
        return String(name || '?').trim().split(/\s+/).slice(0, 2).map(w => w[0] || '').join('').toUpperCase() || '?';
    }

    /**
     * Regel onder de bedrijfsnaam. Het ontwerp zette hier ook een vestigingsplaats, maar
     * bedrijven hebben geen plaats- of omschrijvingsveld; dat verzinnen zou een leeg of
     * verkeerd gegeven tonen. Blijft over: hoeveel labs eraan hangen.
     */
    _detailSubtitle() {
        const count = this.labConnections?.length ?? 0;
        return count === 1 ? '1 labkoppeling' : `${count} labkoppelingen`;
    }

    _renderCompanyTab() {
        return html`
            <div style="margin-bottom:16px;">
              <div class="mv-caps" style="margin-bottom:6px;">BEDRIJFSNAAM</div>
              ${this.editingField === 'name' ? html`
                <div style="display:flex; gap:8px;">
                  <input class="mv-field" .value=${this.editValue} @input=${e => this.editValue = e.target.value}
                    style="flex:1;">
                  <button @click=${this.saveName} ?disabled=${this.busy} style="padding:8px 12px; border:none; border-radius:8px; background:var(--mv-primary); color:#fff; font-weight:600; cursor:pointer;">Opslaan</button>
                  <button @click=${this.cancelEdit} style="padding:8px 12px; border:1px solid var(--mv-border); border-radius:8px; background:none; color:var(--mv-text-sub,#6c757d); cursor:pointer;">&times;</button>
                </div>
              ` : html`
                <div class="mv-field" style="display:flex; justify-content:space-between; align-items:center;">
                  <span style="color:var(--mv-text); font-weight:600;">${this.detailCompany.name}</span>
                  <button @click=${() => this.startEdit('name')} title="Bewerken" style="background:none; border:none; color:#7239EA; cursor:pointer; display:flex;">${unsafeSVG(lucideIcon(Pencil, 16))}</button>
                </div>
              `}
            </div>

            <div style="margin-bottom:20px;">
              <div class="mv-caps" style="margin-bottom:6px;">DB BINDING</div>
              ${this.editingField === 'binding' ? html`
                <div style="display:flex; gap:8px;">
                  <input class="mv-field" .value=${this.editValue} @input=${e => this.editValue = e.target.value}
                    style="flex:1; font-family:monospace;">
                  <button @click=${this.saveBinding} ?disabled=${this.busy} style="padding:8px 12px; border:none; border-radius:8px; background:var(--mv-primary); color:#fff; font-weight:600; cursor:pointer;">Opslaan</button>
                  <button @click=${this.cancelEdit} style="padding:8px 12px; border:1px solid var(--mv-border); border-radius:8px; background:none; color:var(--mv-text-sub,#6c757d); cursor:pointer;">&times;</button>
                </div>
              ` : html`
                <div class="mv-field" style="display:flex; justify-content:space-between; align-items:center;">
                  <code style="font-size:var(--mv-text-base); color:var(--mv-text);">${this.detailCompany.db_binding}</code>
                  <button @click=${() => this.startEdit('binding')} title="Bewerken" style="background:none; border:none; color:#7239EA; cursor:pointer; display:flex;">${unsafeSVG(lucideIcon(Pencil, 16))}</button>
                </div>
              `}
            </div>

            <div style="margin-bottom:20px;">
              <div class="mv-caps" style="margin-bottom:6px;">LICENTIE</div>
              <select class="mv-field mv-select" .value=${this.detailCompany.license || 'geovoice'} ?disabled=${this.busy}
                @change=${(e) => this.saveLicense(e.target.value)}
                style="width:100%; font-weight:600; cursor:pointer;">
                <option value="geovoice" ?selected=${(this.detailCompany.license || 'geovoice') === 'geovoice'}>GeoVoice</option>
                <option value="geodesk" ?selected=${this.detailCompany.license === 'geodesk'}>GeoDesk</option>
              </select>
            </div>

            <div style="margin-bottom:20px;">
              <div class="mv-caps" style="margin-bottom:6px;">MEEKIJKEN</div>
              <div style="display:flex; justify-content:space-between; align-items:center; gap:12px; padding:14px 16px; border:1px solid var(--mv-border); border-radius:10px;">
                <div>
                  <div style="font-weight:600; color:var(--mv-text);">Systeembeheerders mogen meekijken</div>
                  <div style="font-size:var(--mv-text-sm); color:var(--mv-text-sub,#6c757d); max-width:340px;">
                    Geeft systeembeheerders van GeoVoice leesrechten op de gegevens van dit bedrijf,
                    voor support. Ze kunnen niets wijzigen. Het bedrijf kan dit zelf omzetten op de
                    Team-pagina.
                  </div>
                </div>
                ${this._toggle(!!this.detailCompany.sysadmin_access, (v) => this.saveSysadminAccess(v))}
              </div>
            </div>

            <div style="margin-bottom:20px;">
              <div class="mv-caps" style="margin-bottom:6px;">BÈTAFUNCTIES</div>
              ${this._renderBetaInvoer(this.detailCompany)}
            </div>

            ${this.detailCompany.id !== '1' ? html`
              <div style="display:flex; justify-content:flex-end; padding-top:16px; margin-top:16px; border-top:1px solid var(--mv-border);">
                <button @click=${async () => { await this.toggleStatus(this.detailCompany.id, !!this.detailCompany.is_active); this.detailCompany = { ...this.detailCompany, is_active: this.detailCompany.is_active ? 0 : 1 }; }}
                  style="padding:8px 14px; border-radius:8px; border:1px solid ${this.detailCompany.is_active ? 'var(--mv-danger,#dc3545)' : 'var(--mv-primary)'}; background:none; color:${this.detailCompany.is_active ? 'var(--mv-danger,#dc3545)' : 'var(--mv-primary)'}; font-weight:600; cursor:pointer;">
                  ${this.detailCompany.is_active ? 'Bedrijf deactiveren' : 'Bedrijf activeren'}
                </button>
              </div>
            ` : ''}`;
    }

    // ── AI-kosten ───────────────────────────────────────────────────────────
    //
    // Een SCHATTING: het verbruik (tokens, minuten) is gemeten, de omrekening naar dollars komt
    // uit de handmatig bijgehouden prijslijst (@mv/models, modelPricing.ts), dezelfde als in
    // /admin/analysis. Daarom overal '≈' en de uitleg in de tooltip.

    _renderAiTotaal(t) {
        if (!t) return html`<span style="color:var(--mv-text-muted);">…</span>`;
        if (t.totaal == null) return html`<span style="color:var(--mv-text-muted);" title="Database niet te lezen">—</span>`;
        if (!t.aanroepen) return html`<span style="color:var(--mv-text-muted);">$0</span>`;
        return html`<span style="font-variant-numeric:tabular-nums; font-weight:600; color:var(--mv-text);"
            title=${`${t.aanroepen} AI-aanroepen${t.onbekend ? `, waarvan ${t.onbekend} niet te beprijzen` : ''}. Schatting met de prijslijst.`}>≈ ${fmtDollar(t.totaal)}</span>`;
    }

    /**
     * Kosten over de tijd: een keuze van de periode, drie kerncijfers, gestapelde staven per week
     * (een kleur per functionaliteit, met een kaartje bij hover) en een tabel met het aandeel van
     * elke functionaliteit. Alles volgt de gekozen periode.
     */
    _renderAiTab() {
        if (this.aiDagen === null) return html`<div style="color:var(--mv-text-sub);">AI-kosten laden…</div>`;
        if (this.aiFout) return html`<div style="color:var(--mv-danger);">${this.aiFout}</div>`;
        const bereik = AI_BEREIKEN.find((b) => b.id === this.aiBereik) || AI_BEREIKEN[2];
        const weken = aiWeken(this.aiDagen, bereik.weken);
        const totaal = weken.reduce((s, w) => s + somWeek(w, 'kosten'), 0);
        const aanroepen = weken.reduce((s, w) => s + somWeek(w, 'aanroepen'), 0);
        const actief = AI_FUNCTIES.filter((f) => !f.geen);

        // De y-as op ronde stappen, hooguit drie boven de nul.
        const totalen = weken.map((w) => somWeek(w, 'kosten'));
        const max = Math.max(...totalen, 0.01);
        const stap = [0.05, 0.1, 0.25, 0.5, 1, 2, 5, 10, 20, 50, 100, 200, 500, 1000].find((x) => max / x <= 3) || 1000;
        const top = Math.ceil(max / stap) * stap;
        const ticks = [];
        for (let v = top; v > -stap / 2; v -= stap) ticks.push({ label: v >= 1 || v === 0 ? `$${Math.round(v)}` : fmtDollar(v), pos: `${(Math.max(v, 0) / top) * 100}%` });
        const n = weken.length;
        const labelElke = n > 26 ? Math.ceil(n / 9) : n > 12 ? 3 : n > 6 ? 2 : 1;
        const hover = this.aiHover != null && weken[this.aiHover] ? this.aiHover : null;

        const rijen = AI_FUNCTIES.map((f) => {
            const k = weken.reduce((s, w) => s + (w.kosten[f.id] || 0), 0);
            const a = weken.reduce((s, w) => s + (w.aanroepen[f.id] || 0), 0);
            return { ...f, kosten: k, aanroepen: a, aandeel: totaal ? k / totaal : 0 };
        });

        return html`
          <div class="ai-tab">
            <div class="ai-boven">
              <div class="ai-sub">Schatting op basis van gemeten verbruik</div>
              <div class="ai-bereik" role="group" aria-label="Periode">
                ${AI_BEREIKEN.map((b) => html`
                  <button class=${b.id === bereik.id ? 'aan' : ''} aria-pressed=${b.id === bereik.id}
                          @click=${() => { this.aiBereik = b.id; this.aiHover = null; }}>${b.label}</button>`)}
              </div>
            </div>

            <div class="ai-cijfers">
              <div><span>Kosten</span><strong>≈ ${fmtDollar(totaal)}</strong></div>
              <div><span>Aanroepen</span><strong>${aanroepen.toLocaleString('nl-NL')}</strong></div>
              <div><span>Per aanroep</span><strong>${aanroepen ? `≈ ${fmtDollar(totaal / aanroepen, 3)}` : '—'}</strong></div>
            </div>

            <div class="ai-blok">
              <div class="ai-blokkop">
                <strong>Kosten per week</strong>
                <div class="ai-legenda">
                  ${actief.map((f) => html`<span><i style=${`background:${f.kleur}`}></i>${f.label}</span>`)}
                </div>
              </div>
              <div class="ai-grafiek">
                <div class="ai-as">
                  ${ticks.map((t) => html`<span style=${`bottom:${t.pos}`}>${t.label}</span>`)}
                </div>
                <div>
                  <div class="ai-vlak" @mouseleave=${() => { this.aiHover = null; }}>
                    ${ticks.map((t) => html`<div class="ai-lijn" style=${`bottom:${t.pos}`}></div>`)}
                    <div class="ai-staven">
                      ${weken.map((w, i) => {
                          const t = totalen[i];
                          const delen = actief.filter((f) => (w.kosten[f.id] || 0) > 0);
                          return html`
                            <div class="ai-kolom ${hover === i ? 'hover' : ''}" @mouseenter=${() => { this.aiHover = i; }}>
                              <div class="ai-staaf ${t > 0 ? '' : 'nul'}" style=${`height:${t > 0 ? `${Math.max((t / top) * 100, 1.5)}%` : '2px'}`}>
                                ${delen.map((f) => html`<div style=${`height:${(w.kosten[f.id] / t) * 100}%;background:${f.kleur}`}></div>`)}
                              </div>
                            </div>`;
                      })}
                    </div>
                    ${hover != null ? (() => {
                        const w = weken[hover];
                        const t = totalen[hover];
                        const links = Math.min(Math.max(((hover + 0.5) / n) * 100, 16), 84);
                        return html`
                          <div class="ai-tip" style=${`left:${links}%`}>
                            <div class="ai-tipkop"><span>Week van ${w.kort}</span><strong>${t > 0 ? `≈ ${fmtDollar(t)}` : 'Geen verbruik'}</strong></div>
                            ${actief.filter((f) => (w.kosten[f.id] || 0) > 0 || (w.aanroepen[f.id] || 0) > 0).map((f) => html`
                              <div class="ai-tipregel"><i style=${`background:${f.kleur}`}></i><span>${f.label}</span>
                                <em>${fmtDollar(w.kosten[f.id] || 0)} · ${w.aanroepen[f.id] || 0}×</em></div>`)}
                          </div>`;
                    })() : ''}
                  </div>
                  <div class="ai-labels">
                    ${weken.map((w, i) => html`<span>${(n - 1 - i) % labelElke === 0 ? w.kort : ''}</span>`)}
                  </div>
                </div>
              </div>
            </div>

            <div class="ai-tabel">
              <div class="ai-rij kop"><div>Functionaliteit</div><div>Aandeel</div><div>Aanroepen</div><div>Kosten</div></div>
              ${rijen.map((r) => html`
                <div class="ai-rij ${r.geen ? 'geen' : ''}">
                  <div class="ai-functie">
                    <i style=${`background:${r.kleur}`}></i>
                    <div><strong>${r.label}</strong><span>${r.uitleg}</span></div>
                  </div>
                  <div class="ai-aandeel">
                    <div class="ai-balk"><div style=${`width:${r.aandeel * 100}%;background:${r.kleur}`}></div></div>
                    <span>${r.geen ? '' : aandeelLabel(r.aandeel)}</span>
                  </div>
                  <div class="getal">${r.geen || !r.aanroepen ? '—' : r.aanroepen.toLocaleString('nl-NL')}</div>
                  <div class="getal sterk">${r.geen || !r.kosten ? '—' : `≈ ${fmtDollar(r.kosten)}`}</div>
                </div>`)}
            </div>
          </div>`;
    }

    _renderMembersTab() {
        if (this.membersError) return html`<div style="color:var(--mv-danger,#dc3545); font-size:var(--mv-text-md);">${this.membersError}</div>`;
        if (this.members === null) return html`<div style="color:var(--mv-text-sub,#6c757d); font-size:var(--mv-text-md);">Laden…</div>`;
        if (!this.members.length) return html`<div style="color:var(--mv-text-sub,#6c757d); font-size:var(--mv-text-md);">Geen leden.</div>`;
        // Gesleuteld op id én live(): de backend sorteert op rol, dus na een rolwijziging
        // wisselen rijen van plek. Zonder sleutel hergebruikt Lit de <select> van rij 1 voor
        // wie er nú op rij 1 staat, en zonder live() zet hij .value niet opnieuw als de data
        // gelijk bleef — terwijl de gebruiker het veld net met de hand heeft omgezet. Dan
        // toont elke dropdown de zojuist gekozen rol, tot je de pagina ververst.
        return html`
            <div style="display:flex; flex-direction:column; gap:6px;">
              ${repeat(this.members, m => m.id, m => html`
                <div style="display:flex; justify-content:space-between; align-items:center; gap:10px; padding:10px 12px; border:1px solid var(--mv-border); border-radius:8px;">
                  <span style="color:var(--mv-text); flex:1; min-width:0; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">${m.email}</span>
                  ${m.role === 'owner' ? html`
                    <span style="font-size:var(--mv-text-sm); color:var(--mv-text-sub,#6c757d);" title="Eigenaarschap wijzig je via 'Eigenaarschap overdragen' op de Team-pagina">${this._roleLabel(m.role)}</span>
                  ` : m.role === 'guest' ? html`
                    <!-- Geen dropdown, net als op /team: een gast promoveren tot lid weigert de
                         backend (dan ziet hij opeens alle projecten), en een dropdown met alleen
                         Beheerder/Lid liet het veld leeg omdat 'gast' er niet in stond. Wel
                         verwijderen: dat mag een beheerder ook. -->
                    <span style="font-size:var(--mv-text-sm); color:var(--mv-text-sub,#6c757d);" title="Een gast heeft toegang tot losse projecten; dat regel je via 'Toegang projecten beheren' op de Team-pagina">${this._roleLabel(m.role)}</span>
                    <button @click=${() => this.removeMember(m)} ?disabled=${this.busy} title="Verwijderen uit bedrijf"
                      style="display:flex; align-items:center; justify-content:center; width:30px; height:30px; border-radius:6px; border:1px solid var(--mv-border); background:var(--mv-surface,#fff); color:var(--mv-danger,#dc3545); cursor:pointer; flex-shrink:0;">
                      ${unsafeSVG(lucideIcon(Trash2, 15))}
                    </button>
                  ` : html`
                    <select .value=${live(m.role)} ?disabled=${this.busy}
                      @change=${(e) => this.changeMemberRole(m, e.target.value)}
                      style="padding:5px 8px; border-radius:6px; border:1px solid var(--mv-border); background:var(--mv-surface,#fff); color:var(--mv-text); font-weight:600; font-size:var(--mv-text-sm); cursor:pointer;">
                      <option value="manager" ?selected=${m.role === 'manager'}>Beheerder</option>
                      <option value="member" ?selected=${m.role === 'member'}>Lid</option>
                    </select>
                    <button @click=${() => this.removeMember(m)} ?disabled=${this.busy} title="Verwijderen uit bedrijf"
                      style="display:flex; align-items:center; justify-content:center; width:30px; height:30px; border-radius:6px; border:1px solid var(--mv-border); background:var(--mv-surface,#fff); color:var(--mv-danger,#dc3545); cursor:pointer; flex-shrink:0;">
                      ${unsafeSVG(lucideIcon(Trash2, 15))}
                    </button>
                  `}
                </div>
              `)}
            </div>`;
    }

    _labStatusLabel(status) {
        return status === 'active' ? 'Actief'
            : status === 'invalid' ? 'Ongeldig'
            : status === 'inactive' ? 'Uit'
            : 'Concept';
    }

    _labStatusColor(status) {
        return status === 'active' ? 'var(--mv-primary)'
            : status === 'invalid' ? 'var(--mv-danger,#dc3545)'
            : status === 'inactive' ? 'var(--mv-text-muted,#6c757d)'
            : '#D97706';
    }

    _openLabForm() {
        this.labFormOpen = true;
        this.labForm = { labKey: 'sgs', environment: 'test', username: '', password: '' };
        this.labConnectionsError = null;
    }

    async _reloadLabConnections() {
        if (!this.detailCompany) return;
        const res = await api.getAdminCompanyLabConnections(this.detailCompany.id);
        this.labConnections = res?.labConnections || [];
        this._syncLabConnectionsToList();
    }

    /**
     * Achter de popup staat de bedrijvenlijst met dezelfde koppelingen als tegels. Die zou
     * verouderd achterblijven tot de volgende paginalading, dus werk hem hier bij in plaats
     * van alles opnieuw op te halen.
     */
    _syncLabConnectionsToList() {
        if (!this.detailCompany) return;
        const others = (this.allLabConnections || [])
            .filter(c => String(c.companyId) !== String(this.detailCompany.id));
        this.allLabConnections = [...others, ...(this.labConnections || [])];
    }

    async _createLabConnection(e) {
        e.preventDefault();
        if (!this.detailCompany || !this.labForm.username || !this.labForm.password) return;
        this.labBusy = true;
        this.labConnectionsError = null;
        try {
            const lab = labPresentation(this.labForm.labKey);
            const res = await api.createAdminCompanyLabConnection(this.detailCompany.id, {
                labKey: lab.key,
                displayName: lab.name,
                environment: this.labForm.environment,
                username: this.labForm.username,
                password: this.labForm.password,
            });
            this.labConnections = [...this.labConnections, res.labConnection].filter(Boolean);
            this._syncLabConnectionsToList();
            if (res.labConnection?.status === 'invalid' && res.labConnection?.lastVerifyError) {
                this.labConnectionsError = res.labConnection.lastVerifyError;
            }
            this.labFormOpen = false;
            this.labForm = { labKey: 'sgs', environment: 'test', username: '', password: '' };
        } catch (e) {
            this.labConnectionsError = e.message || 'Labkoppeling opslaan mislukt';
        } finally {
            this.labBusy = false;
        }
    }

    async _verifyLabConnection(conn) {
        this.labBusy = true;
        this.labConnectionsError = null;
        try {
            await api.verifyAdminLabConnection(conn.id);
            await this._reloadLabConnections();
        } catch (e) {
            this.labConnectionsError = e.message || 'Verificatie mislukt';
            await this._reloadLabConnections().catch(() => {});
        } finally {
            this.labBusy = false;
        }
    }

    _openLabCredentialForm(conn) {
        this.labConnectionsError = null;
        this._labCredentialForms = {
            ...this._labCredentialForms,
            [conn.id]: { environment: conn.environment || 'test', username: '', password: '' },
        };
    }

    _closeLabCredentialForm(conn) {
        const next = { ...this._labCredentialForms };
        delete next[conn.id];
        this._labCredentialForms = next;
    }

    async _updateLabCredentials(e, conn) {
        e.preventDefault();
        const form = this._labCredentialForms?.[conn.id];
        if (!form?.username || !form?.password) return;
        this.labBusy = true;
        this.labConnectionsError = null;
        try {
            await api.updateAdminLabConnection(conn.id, {
                environment: form.environment,
                username: form.username,
                password: form.password,
            });
            this._closeLabCredentialForm(conn);
            await api.verifyAdminLabConnection(conn.id);
            await this._reloadLabConnections();
        } catch (e) {
            this.labConnectionsError = e.message || 'Credentials bijwerken mislukt';
            await this._reloadLabConnections().catch(() => {});
        } finally {
            this.labBusy = false;
        }
    }

    async _syncLabProducts(conn) {
        this.labBusy = true;
        this.labConnectionsError = null;
        try {
            await api.syncAdminLabProducts(conn.id);
            await this._reloadLabConnections();
        } catch (e) {
            this.labConnectionsError = e.message || 'Productsync mislukt';
        } finally {
            this.labBusy = false;
        }
    }

    async _deleteLabConnection(conn) {
        const ok = await confirmDialog({
            title: 'Labkoppeling verwijderen?',
            message: `Je verwijdert de ${conn.displayName || conn.labKey}-koppeling voor ${this.detailCompany.name}. Projectgebruikers kunnen daarna geen nieuwe labopdrachten via deze koppeling maken.`,
            confirmLabel: 'Verwijderen',
            danger: true,
        });
        if (!ok) return;
        this.labBusy = true;
        try {
            await api.deleteAdminLabConnection(conn.id);
            this.labConnections = this.labConnections.filter(x => x.id !== conn.id);
            this._syncLabConnectionsToList();
        } catch (e) {
            this.labConnectionsError = e.message || 'Verwijderen mislukt';
        } finally {
            this.labBusy = false;
        }
    }

    /** "vandaag, 09:41" leest prettiger dan een volledige datum bij iets van vijf minuten terug. */
    _labVerifiedText(iso) {
        if (!iso) return null;
        // Via parseTimestamp: een kale tijd uit de database is UTC, en die hier als lokale tijd
        // lezen schoof hem een paar uur op -- een fout die in Nederland een uur kostte en in
        // Toronto zes.
        const d = parseTimestamp(iso);
        if (!d) return null;
        const time = formatTime(d);
        const today = new Date();
        const sameDay = d.toDateString() === today.toDateString();
        return sameDay ? `vandaag, ${time}` : `${d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}, ${time}`;
    }

    _renderLabConnections() {
        return html`
            <div style="display:flex; justify-content:space-between; align-items:flex-start; gap:16px; margin-bottom:16px;">
              <div>
                <div style="font-size:var(--mv-text-lg); font-weight:var(--mv-weight-bold); letter-spacing:var(--mv-tracking-snug); color:var(--mv-text);">Koppelingen met laboratoria</div>
                <div style="font-size:var(--mv-text-sm); line-height:1.5; color:var(--mv-text-sub); margin-top:4px; max-width:400px;">
                  Inloggegevens worden alleen opgeslagen, nooit teruggelezen.
                  Projectgebruikers zien uitsluitend of een koppeling actief is.
                </div>
              </div>
              <button class="mv-btn mv-btn-sm mv-btn-primary" style="white-space:nowrap;"
                @click=${this._openLabForm} ?disabled=${this.labBusy}>
                ${unsafeSVG(lucideIcon(Plus, 14))} Koppeling toevoegen
              </button>
            </div>

            ${this.labConnectionsError ? html`
              <div style="margin-bottom:12px; padding:10px 12px; border-radius:8px; background:var(--mv-danger-bg,#fff5f5); color:var(--mv-danger,#dc3545); font-size:var(--mv-text-base); font-weight:600;">
                ${this.labConnectionsError}
              </div>` : ''}

            ${this.labFormOpen ? this._renderLabForm() : ''}

            ${this.labConnections.length === 0 && !this.labFormOpen ? html`
              <div style="padding:16px; border:1px dashed var(--mv-border); border-radius:10px; color:var(--mv-text-sub,#6c757d); font-size:var(--mv-text-base);">
                Nog geen labkoppeling. Projectgebruikers zien de supportmelding op de projectpagina.
              </div>
            ` : html`
              <div style="display:flex; flex-direction:column; gap:10px;">
                ${this.labConnections.map(conn => this._renderLabCard(conn))}
              </div>`}`;
    }

    _renderLabForm() {
        const chosen = labPresentation(this.labForm.labKey);
        return html`
            <form @submit=${this._createLabConnection}
              style="border:1.5px dashed var(--mv-border-dashed); border-radius:var(--mv-radius-lg); padding:18px; margin-bottom:14px; background:var(--mv-surface-alt);">
              <div class="mv-caps" style="margin-bottom:8px;">LABORATORIUM</div>
              <div style="display:flex; gap:10px; margin-bottom:14px; flex-wrap:wrap;">
                ${SELECTABLE_LABS.map(lab => html`
                  <button type="button" @click=${() => this.labForm = { ...this.labForm, labKey: lab.key }}
                    style="display:flex; align-items:center; gap:10px; padding:10px 14px; border-radius:10px; cursor:pointer; background:var(--mv-surface,#fff); border:2px solid ${this.labForm.labKey === lab.key ? 'var(--mv-primary)' : 'var(--mv-border)'};">
                    ${this._renderLabMark(lab, 28)}
                    <span style="font-weight:700; color:var(--mv-text);">${lab.name}</span>
                  </button>`)}
              </div>

              <div style="display:grid; grid-template-columns:130px 1fr 1fr; gap:8px;">
                <select .value=${this.labForm.environment} @change=${e => this.labForm = { ...this.labForm, environment: e.target.value }}
                  style="padding:9px; border-radius:8px; border:1px solid var(--mv-border); background:var(--mv-surface,#fff); color:var(--mv-text);">
                  <option value="test" ?selected=${this.labForm.environment === 'test'}>Testomgeving</option>
                  <option value="production" ?selected=${this.labForm.environment === 'production'}>Productie</option>
                </select>
                <input type="text" autocomplete="off" placeholder=${`${chosen.name} gebruikersnaam`} .value=${this.labForm.username}
                  @input=${e => this.labForm = { ...this.labForm, username: e.target.value }}
                  style="padding:9px 10px; border-radius:8px; border:1px solid var(--mv-border); background:var(--mv-surface,#fff); color:var(--mv-text);">
                <input type="password" autocomplete="new-password" placeholder=${`${chosen.name} wachtwoord`} .value=${this.labForm.password}
                  @input=${e => this.labForm = { ...this.labForm, password: e.target.value }}
                  style="padding:9px 10px; border-radius:8px; border:1px solid var(--mv-border); background:var(--mv-surface,#fff); color:var(--mv-text);">
              </div>
              <div style="display:flex; justify-content:flex-end; gap:8px; margin-top:12px;">
                <button type="button" @click=${() => this.labFormOpen = false}
                  style="padding:8px 13px; border-radius:8px; border:1px solid var(--mv-border); background:var(--mv-surface,#fff); color:var(--mv-text); cursor:pointer;">Annuleren</button>
                <button type="submit" ?disabled=${this.labBusy || !this.labForm.username || !this.labForm.password}
                  style="padding:8px 13px; border-radius:8px; border:none; background:var(--mv-primary); color:#fff; font-weight:700; cursor:pointer; ${this.labBusy ? 'opacity:.55;' : ''}">
                  Opslaan en verifiëren
                </button>
              </div>
            </form>`;
    }

    _renderLabCard(conn) {
        const lab = labPresentation(conn.labKey);
        const dot = labStatusDot(conn);
        const open = this.expandedLab === conn.id;
        const verified = this._labVerifiedText(conn.lastVerifiedAt);
        const credentialForm = this._labCredentialForms?.[conn.id];
        return html`
            <div style="border:1px solid var(--mv-border); border-radius:12px; overflow:hidden;">
              <div @click=${() => this.expandedLab = open ? null : conn.id}
                style="display:flex; align-items:center; gap:14px; padding:14px 16px; cursor:pointer;">
                ${this._renderLabMark(lab, 44)}
                <div style="min-width:0; flex:1;">
                  <div style="display:flex; align-items:center; gap:8px; flex-wrap:wrap;">
                    <span style="font-size:var(--mv-text-lg); font-weight:var(--mv-weight-heavy); letter-spacing:var(--mv-tracking-snug); color:var(--mv-text);">${lab.name}</span>
                    <span class="mv-pill" style="background:${dot.color}1A; color:${dot.color};">
                      <span class="mv-dot" style="background:${dot.color};"></span>
                      ${this._labStatusLabel(conn.status).toUpperCase()}
                    </span>
                    <span class="mv-pill">${conn.environment === 'production' ? 'Productie' : 'Testomgeving'}</span>
                  </div>
                  <div style="font-size:var(--mv-text-base); color:var(--mv-text-sub,#6c757d); margin-top:3px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">
                    ${conn.customerId ? html`Klantnummer ${conn.customerId}` : 'Nog niet geverifieerd'}${verified ? ` · geverifieerd ${verified}` : ''}
                  </div>
                </div>
                <span style="color:var(--mv-text-sub,#6c757d); display:flex; flex:none;">
                  ${unsafeSVG(lucideIcon(open ? ChevronUp : ChevronDown, 18))}
                </span>
              </div>

              ${!open ? '' : html`
                <div style="padding:16px 18px 18px; border-top:1px solid var(--mv-border-soft);">
                  ${conn.lastVerifyError ? html`
                    <div style="margin-bottom:14px; padding:10px 12px; border-radius:8px; background:var(--mv-danger-bg,#fff5f5); color:var(--mv-danger,#dc3545); font-size:var(--mv-text-base);">
                      ${conn.lastVerifyError}
                    </div>` : ''}

                  <div style="display:grid; grid-template-columns:repeat(auto-fit,minmax(150px,1fr)); gap:16px; margin-bottom:16px;">
                    ${[
                      ['KLANTNUMMER', conn.customerId || '—'],
                      ['LAB-ID', conn.labId || '—'],
                      ['GEBRUIKERSNAAM', conn.usernameMasked ? `${conn.usernameMasked}••••` : '—'],
                      ['LAATST GEVERIFIEERD', verified ? verified.charAt(0).toUpperCase() + verified.slice(1) : '—'],
                      // Wat de productlijst heeft opgeleverd. Zonder deze regel is niet te zien
                      // waarom de urgentielijst in een labopdracht leeg blijft.
                      ['PRODUCTLIJST', conn.productSyncedAt
                        ? `${(conn.urgencies || []).length} urgenties · ${(conn.agreements || []).length} afspraken${conn.productVersion ? ` · versie ${conn.productVersion}` : ''}`
                        : 'Nog niet opgehaald'],
                    ].map(([label, value]) => html`
                      <div>
                        <div class="mv-caps">${label}</div>
                        <div style="font-size:var(--mv-text-md); font-weight:var(--mv-weight-semi); color:var(--mv-text); margin-top:5px; word-break:break-word;">${value}</div>
                      </div>`)}
                  </div>

                  ${credentialForm ? html`
                    <form @submit=${(e) => this._updateLabCredentials(e, conn)}
                      style="display:grid; grid-template-columns:1fr 1fr auto; gap:8px; margin-bottom:14px;">
                      <input type="text" autocomplete="off" placeholder="Nieuwe gebruikersnaam" .value=${credentialForm.username}
                        @input=${e => this._labCredentialForms = { ...this._labCredentialForms, [conn.id]: { ...credentialForm, username: e.target.value } }}
                        style="padding:9px 10px; border-radius:8px; border:1px solid var(--mv-border); background:var(--mv-surface,#fff); color:var(--mv-text);">
                      <input type="password" autocomplete="new-password" placeholder="Nieuw wachtwoord" .value=${credentialForm.password}
                        @input=${e => this._labCredentialForms = { ...this._labCredentialForms, [conn.id]: { ...credentialForm, password: e.target.value } }}
                        style="padding:9px 10px; border-radius:8px; border:1px solid var(--mv-border); background:var(--mv-surface,#fff); color:var(--mv-text);">
                      <div style="display:flex; gap:6px;">
                        <button type="submit" ?disabled=${this.labBusy || !credentialForm.username || !credentialForm.password}
                          style="padding:8px 12px; border-radius:8px; border:none; background:var(--mv-primary); color:#fff; font-weight:700; cursor:pointer;">Opslaan</button>
                        <button type="button" @click=${() => this._closeLabCredentialForm(conn)}
                          style="padding:8px 12px; border-radius:8px; border:1px solid var(--mv-border); background:var(--mv-surface,#fff); color:var(--mv-text-sub,#6c757d); cursor:pointer;">&times;</button>
                      </div>
                    </form>` : ''}

                  <!-- Eén regel: dit zijn nevengeschikte acties op dezelfde koppeling, en
                       afbreken naar twee regels laat de kaart uitdijen zonder dat het iets
                       zegt. Verwijderen staat als los icoon rechts: zelden nodig, en het
                       hoort niet even breed te zijn als testen. -->
                  <div style="display:flex; gap:8px; align-items:center;">
                    <button class="mv-btn mv-btn-sm mv-btn-accent" @click=${() => this._verifyLabConnection(conn)} ?disabled=${this.labBusy}>
                      ${unsafeSVG(lucideIcon(RefreshCw, 13))} Verbinding testen
                    </button>
                    <button class="mv-btn mv-btn-sm" @click=${() => this._syncLabProducts(conn)}
                      ?disabled=${this.labBusy || conn.status !== 'active'}
                      title=${conn.status === 'active'
                        ? 'Haalt de urgenties en afspraken op die dit lab voor deze klantcode toestaat'
                        : 'Verifieer de koppeling eerst'}>
                      ${unsafeSVG(lucideIcon(ListChecks, 13))} Productlijst
                    </button>
                    <button class="mv-btn mv-btn-sm" @click=${() => this._openLabCredentialForm(conn)} ?disabled=${this.labBusy}>
                      ${unsafeSVG(lucideIcon(KeyRound, 13))} Gegevens
                    </button>
                    <button class="mv-btn mv-btn-sm mv-btn-icon mv-btn-danger" style="margin-left:auto;"
                      @click=${() => this._deleteLabConnection(conn)} ?disabled=${this.labBusy}
                      title="Koppeling verwijderen" aria-label="Koppeling verwijderen">
                      ${unsafeSVG(lucideIcon(Trash2, 14))}
                    </button>
                  </div>
                </div>`}
            </div>`;
    }

    handleSearchInput(e) {
        this.searchQuery = e.target.value.toLowerCase();
        this.filterCompanies();
    }

    filterCompanies() {
        if (!this.searchQuery) {
            this.filteredCompanies = [...this.companies];
        } else {
            this.filteredCompanies = this.companies.filter(c =>
                (c.name || '').toLowerCase().includes(this.searchQuery)
            );
        }
    }

    get nextAvailableBinding() {
        if (this.capacity) return this.capacity.next;
        const used = new Set(this.companies.map(c => c.db_binding));
        return AVAILABLE_BINDINGS.find(b => !used.has(b)) || null;
    }

    /** Aantal bedrijfsdatabases dat de Worker kent, en hoeveel daarvan bezet zijn. */
    get capacityText() {
        const total = this.capacity?.total ?? AVAILABLE_BINDINGS.length;
        const used = this.capacity?.used ?? new Set(
            this.companies.map(c => c.db_binding).filter(b => AVAILABLE_BINDINGS.includes(b))
        ).size;
        return { total, used, full: used >= total };
    }

    openCreateModal() {
        // Bij het OPENEN bepalen of het vol is, niet tijdens het tekenen. Anders sloeg het
        // om terwijl het venster al openstond: wie het laatste bedrijf aanmaakte kreeg geen
        // bevestiging te zien maar de melding dat het vol is — precies op het moment dat het
        // wél gelukt was. Je hoort het pas te horen als je het opnieuw probeert.
        this.noCapacity = !this.nextAvailableBinding;
        this.isCreateModalOpen = true;
        this.newCompanyName = '';
        this.newAdminEmail = '';
        // Elke keer opnieuw op de basislicentie: blijft de vorige keuze staan, dan deelt de
        // volgende aanmaak ongemerkt GeoDesk uit.
        this.newLicense = 'geovoice';
        this.newAdminFirstName = '';
        this.newAdminLastName = '';
        this.newDbBinding = '';
        this.createdResult = null;
        this.error = null;
    }

    closeCreateModal() {
        this.isCreateModalOpen = false;
        this.createdResult = null;
    }

    async handleCreateCompany(e) {
        e.preventDefault();
        if (!this.newCompanyName || !this.newAdminEmail || !this.newAdminFirstName.trim() || !this.newAdminLastName.trim()) return;

        const binding = this.nextAvailableBinding;
        // Kan gebeuren als de laatste database bezet raakte terwijl dit venster openstond.
        // Stil teruggeven laat de knop 'Aanmaken' doen alsof hij kapot is.
        if (!binding) {
            this.error = 'Er is geen vrije bedrijfsdatabase meer. Vraag om meer bedrijfsdatabases.';
            return;
        }

        try {
            this.isCreating = true;
            this.error = null;

            const response = await api.createCompany({
                name: this.newCompanyName,
                adminEmail: this.newAdminEmail,
                firstName: this.newAdminFirstName.trim(),
                lastName: this.newAdminLastName.trim(),
                db_binding: binding,
                license: this.newLicense || 'geovoice'
            });

            if (response && response.success) {
                // No generated password — the admin gets an invitation (set their own
                // password) or, if they already have an account, is added directly.
                this.createdResult = {
                    company: response.company.name,
                    email: response.admin?.email || this.newAdminEmail,
                    invited: !!response.admin?.invited,
                    added: !!response.admin?.added,
                    emailed: !!response.admin?.emailed,
                    token: response.admin?.token || null,
                };
                // Reload list in background
                this.loadCompanies();
                // Maakte je een bedrijf aan met jezelf als beheerder, dan hoor je er nu bij --
                // en dan hoort het meteen in de bedrijvenkiezer te staan, zonder opnieuw
                // inloggen. Voor een ander e-mailadres verandert er niets aan jouw lijst.
                auth.refreshCompanies().catch(() => {});
            } else {
                throw new Error('Fout bij aanmaken bedrijf');
            }

        } catch (error) {
            this.error = error.message || 'Mislukt om bedrijf aan te maken.';
        } finally {
            this.isCreating = false;
        }
    }

    async copyInviteLink(token) {
        if (!token) return;
        const text = `${window.location.origin}/accept-invite?token=${token}`;
        try {
            await navigator.clipboard.writeText(text);

            // Visual feedback
            const btn = this.shadowRoot.querySelector('#copy-btn');
            if (btn) {
                const originalHtml = btn.innerHTML;
                btn.innerHTML = `${unsafeSVG(lucideIcon(Check, 16))} Gekopieerd!`;
                btn.style.background = '#10B981';
                btn.style.color = 'white';
                btn.style.borderColor = '#10B981';
                setTimeout(() => {
                    btn.innerHTML = originalHtml;
                    btn.style.background = '';
                    btn.style.color = '';
                    btn.style.borderColor = '';
                }, 2000);
            }
        } catch (err) {
            console.error('Failed to copy text: ', err);
        }
    }

    async toggleStatus(companyId, currentStatus) {
        if (companyId === '1') return; // Cannot toggle MS Main Company
        try {
            const newStatus = !currentStatus;
            await api.updateCompanyStatus(companyId, newStatus);

            const origCompany = this.companies.find(c => c.id === companyId);
            if (origCompany) {
                origCompany.is_active = newStatus ? 1 : 0;
            }
            this.filterCompanies();
        } catch (err) {
            alertDialog({ message: `Actie mislukt: ${err.message}` });
        }
    }

    openEditModal(company) {
        this.editTarget = company;
        this.editName = company.raw_name || company.name;
        this.editDbBinding = company.raw_db_binding || '';
        this.isEditModalOpen = true;
        this.error = null;
    }

    closeEditModal() {
        this.isEditModalOpen = false;
        setTimeout(() => { this.editTarget = null; }, 300);
    }

    async handleEditCompany(e) {
        e.preventDefault();
        if (!this.editTarget) return;
        try {
            this.isSaving = true;
            this.error = null;
            await api.updateCompany(this.editTarget.id, {
                name: this.editName,
                db_binding: this.editDbBinding.trim()
            });
            await this.loadCompanies();
            this.closeEditModal();
        } catch (err) {
            this.error = err.message || 'Opslaan mislukt.';
        } finally {
            this.isSaving = false;
        }
    }

    formatDate(dateStr) {
        if (!dateStr) return '-';
        // Dutch timezone, DD-MM-YYYY (DB timestamps are UTC).
        return formatDateNL(dateStr, { month: '2-digit' });
    }

    /**
     * Wat je ziet als er geen database vrij is.
     *
     * Elk bedrijf krijgt een eigen database, en die maakt de applicatie niet zelf aan. Er is
     * dus een grens, en die is bereikt. Dat hoort hier te staan in plaats van in een tooltip
     * bij een uitgeschakelde knop -- "de knop doet het niet" is precies de indruk die we niet
     * willen wekken.
     *
     * Wat er NIET in staat is hoe je een database aanmaakt. Wie dit leest is niet degene die
     * dat doet; die moet het alleen kunnen vragen. Een lijstje wrangler-commando's is voor
     * hem ruis waar hij niets mee kan.
     */
    renderNoCapacity() {
        const { used, total } = this.capacityText;
        const inactive = this.capacity?.heldByInactive ?? 0;
        return html`
          <div class="modal-header">
            <h2 style="display:flex; align-items:center; gap:8px; color:#D97706;">
              ${unsafeSVG(lucideIcon(AlertCircle, 20))} Geen vrije bedrijfsdatabase
            </h2>
            <button class="close-btn" @click=${this.closeCreateModal}>&times;</button>
          </div>
          <div class="modal-body">
            <p style="margin:0 0 1rem; font-size:var(--mv-text-md); color:var(--mv-text); line-height:1.55;">
              Alle <strong>${used} van de ${total}</strong> bedrijfsdatabases zijn aan een bedrijf gekoppeld.
              Een nieuw bedrijf krijgt altijd een eigen database, dus er kan er pas weer een bij
              als er een database klaarstaat.
            </p>
            <p style="margin:0 0 1rem; padding:0.8rem 1rem; background:#FEF3C7; border-radius:8px; font-size:var(--mv-text-md); font-weight:600; color:#92400E; line-height:1.5;">
              Vraag om meer bedrijfsdatabases.
            </p>
            ${inactive > 0 ? html`
              <p style="margin:0 0 1.25rem; font-size:var(--mv-text-base); color:var(--mv-text-sub); line-height:1.5;">
                ${inactive === 1 ? 'Eén van deze databases hoort' : `${inactive} van deze databases horen`}
                bij een gedeactiveerd bedrijf. Een bedrijf uitzetten geeft zijn database niet vrij,
                want de gegevens blijven staan.
              </p>
            ` : ''}
            <div style="display:flex; justify-content:flex-end;">
              <button class="btn-secondary" @click=${this.closeCreateModal}>Sluiten</button>
            </div>
          </div>
        `;
    }

    static styles = css`${unsafeCSS(base)}${unsafeCSS(styles)}`;

    render() {
        return html`
      <div class="dashboard-header" style="display:flex; justify-content:space-between; align-items:center; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem;">
        <div>
          <h1 class="mv-h1">Bedrijven en labkoppelingen</h1>
          <p class="mv-lede" style="margin-top:6px;">Per bedrijf zie je in één blik met welke laboratoria er een koppeling ligt. Klik een rij open voor de details.</p>
          ${this.isLoading ? '' : html`
            <div style="margin-top:4px; font-size:var(--mv-text-sm); color:${this.capacityText.full ? '#D97706' : 'var(--mv-text-muted,#5E6278)'}; display:flex; align-items:center; gap:5px;">
              ${this.capacityText.full ? unsafeSVG(lucideIcon(AlertCircle, 14)) : ''}
              ${this.capacityText.used} van de ${this.capacityText.total} bedrijfsdatabases in gebruik${this.capacityText.full ? ', er kan geen bedrijf meer bij' : ''}
            </div>
          `}
        </div>
        <!-- De knop blijft klikbaar als het vol is: een dode knop met alleen een tooltip
             bij hover ziet eruit als een storing. Klikken vertelt nu wat eraan schort. -->
        <sl-tooltip content="${this.nextAvailableBinding ? 'Nieuw Bedrijf' : 'Geen vrije bedrijfsdatabase'}" placement="left">
          <button class="create-icon-btn" @click=${() => this.openCreateModal()}>
            ${unsafeSVG(lucideIcon(Plus, 24))}
          </button>
        </sl-tooltip>
      </div>

      <div class="search-container">
        <div class="search-input-wrapper">
          <span class="search-icon">
            ${unsafeSVG(lucideIcon(Search, 16))}
          </span>
          <input 
            type="text" 
            class="search-input"
            placeholder="Zoeken op bedrijfsnaam..." 
            .value=${this.searchQuery}
            @input=${this.handleSearchInput}
          />
        </div>
      </div>

      <div class="table-card">
        ${this.isLoading ? html`
          <div class="table-loading">Laden...</div>
        ` : this.error && !this.isCreateModalOpen ? html`
          <div class="table-error">
            ${unsafeSVG(lucideIcon(AlertCircle, 18))} ${this.error}
          </div>
        ` : html`
          <mv-table
            @row-click=${(e) => this.openDetail(e.detail.row)}
            .columns=${[
                    { id: 'name', title: 'Bedrijfsnaam', sortable: true },
                    { id: 'db_binding', title: 'DB Binding', sortable: true },
                    { id: 'license', title: 'Licentie', sortable: true },
                    { id: 'lab_connections', title: 'Labkoppelingen', sortable: false },
                    { id: 'beta_features', title: 'Bètafuncties', sortable: false },
                    { id: 'ai_kosten', title: 'AI-kosten', sortValue: (r) => r.raw_ai_kosten ?? -1, defaultSortDir: 'desc' },
                    { id: 'is_active', title: 'Status', sortable: true },
                    { id: 'created_at', title: 'Geregistreerd Op', sortable: true },
                ]}
            .data=${this.filteredCompanies.map(c => ({
                    ...c,
                    raw_name: c.name,
                    raw_is_active: c.is_active,
                    raw_db_binding: c.db_binding,
                    // Sorteren op de ruwe waarde; in beeld staat het label.
                    raw_license: c.license || 'geovoice',
                    // Twee kleuren die niets over rangorde zeggen: blauw en oranje staan naast
                    // elkaar, waar groen naast grijs leest als 'beter' naast 'minder'.
                    // Vorm komt van .status-pill uit mv-table, net als de kolom Status: geen rand,
                    // alleen een zacht vlak met bijpassende tekstkleur. Hier alleen de tint, zodat
                    // de twee kolommen niet elk hun eigen tegel uitvinden.
                    license: (() => {
                        const isGeoDesk = (c.license || 'geovoice') === 'geodesk';
                        const kleur = isGeoDesk
                            ? { tekst: '#C2410C', vlak: '#FFF3E8' }
                            : { tekst: '#1D4ED8', vlak: '#EDF3FE' };
                        return html`<span class="status-pill" style="background:${kleur.vlak}; color:${kleur.tekst};">
                          ${isGeoDesk ? 'GeoDesk' : 'GeoVoice'}
                        </span>`;
                    })(),
                    db_binding: html`<code style="font-size:var(--mv-text-sm); background: var(--mv-bg); padding: 2px 6px; border-radius: 4px; color: var(--mv-text-sub);">${c.db_binding}</code>`,
                    name: html`<strong style="color: var(--mv-text); font-weight: 600;">${c.name}</strong>`,
                    created_at: html`<span style="color: var(--mv-text-sub);">${this.formatDate(c.created_at)}</span>`,
                    beta_features: this._renderBetaTegels(c),
                    raw_ai_kosten: this.aiTotalen[c.id]?.totaal ?? null,
                    ai_kosten: this._renderAiTotaal(this.aiTotalen[c.id]),
                    lab_connections: (() => {
                        const labs = this._labsForCompany(c.id);
                        if (!labs.length) return html`<span style="color:var(--mv-text-sub,#6c757d); font-size:var(--mv-text-base);">Geen koppeling</span>`;
                        return html`<span style="display:flex; flex-wrap:wrap; gap:6px;">${labs.map(conn => this._renderLabChip(conn))}</span>`;
                    })(),
                    is_active: html`
                <span class="status-pill ${c.is_active || c.id === '1' ? 'status-active' : 'status-inactive'}">
                  ${c.is_active || c.id === '1' ? 'Actief' : 'Gedeactiveerd'}
                </span>
              `
                }))}
            .pageSize=${20}
          ></mv-table>
        `}
      </div>

      <!-- Create Modal -->
      <div class="modal-overlay ${this.isCreateModalOpen ? 'open' : ''}">
        <div class="modal-content">
          ${this.createdResult ? html`
            <div class="modal-header">
              <h2 style="color: #10B981; display:flex; align-items:center; gap:8px;">${unsafeSVG(lucideIcon(Check, 20))} Bedrijf Aangemaakt</h2>
              <button class="close-btn" @click=${this.closeCreateModal}>&times;</button>
            </div>
            <div class="modal-body">
              <div class="success-panel">
                <p style="margin-top:0; color:#065F46;">
                  <strong>${this.createdResult.company}</strong> is aangemaakt.
                  ${this.createdResult.added
                    ? html` ${this.createdResult.email} had al een account en is direct als eigenaar toegevoegd${this.createdResult.emailed ? ' en per e-mail op de hoogte gebracht' : ''}.`
                    : this.createdResult.emailed
                      ? html` Er is een uitnodiging gestuurd naar ${this.createdResult.email} om een wachtwoord in te stellen en het beheer over te nemen.`
                      : html` De uitnodiging voor ${this.createdResult.email} kon niet per e-mail worden verstuurd — deel de onderstaande link zelf.`}
                </p>

                ${this.createdResult.invited && this.createdResult.token ? html`
                  <div style="display:flex; gap:8px; margin-top:12px;">
                    <input readonly .value=${`${window.location.origin}/accept-invite?token=${this.createdResult.token}`}
                      style="flex:1; padding:8px 10px; border-radius:8px; border:1px solid var(--mv-border); background:var(--mv-surface,#fff); color:var(--mv-text); font-size:var(--mv-text-sm);" />
                    <button class="btn-secondary" @click=${() => this.copyInviteLink(this.createdResult.token)}
                      style="display:flex; align-items:center; gap:6px; white-space:nowrap;">
                      ${unsafeSVG(lucideIcon(Copy, 16))} Kopieer link
                    </button>
                  </div>
                ` : ''}
              </div>
            </div>
          ` : this.noCapacity ? this.renderNoCapacity() : html`
            <div class="modal-header">
              <h2>Nieuw Bedrijf Toevoegen</h2>
              <button class="close-btn" @click=${this.closeCreateModal}>&times;</button>
            </div>
            <div class="modal-body">
              <form @submit=${this.handleCreateCompany}>
                ${this.error ? html`
                  <div style="padding: 1rem; background: #FFF5F8; color: #F1416C; border-radius: 0.5rem; margin-bottom: 1rem; font-size:var(--mv-text-md);">
                    ${this.error}
                  </div>
                ` : ''}
                
                <div class="form-group">
                  <label>Bedrijfsnaam</label>
                  <input type="text" 
                         .value=${this.newCompanyName} 
                         @input=${e => this.newCompanyName = e.target.value}
                         placeholder="Naam van het ingenieursbureau" required />
                </div>
                
                <div style="padding: 0.75rem 1rem; background: var(--mv-surface-alt); border-radius: var(--mv-radius-sm); font-size: var(--mv-text-md); color: var(--mv-text-sub);">
                  Binding: <code style="font-weight: 600; color: #7239EA;">${this.nextAvailableBinding}</code>
                </div>

                <div class="form-group" style="margin-top: 1rem;">
                  <label>Licentie</label>
                  <select .value=${this.newLicense}
                    @change=${e => this.newLicense = e.target.value}
                    style="cursor:pointer;">
                    <option value="geovoice" ?selected=${this.newLicense !== 'geodesk'}>GeoVoice</option>
                    <option value="geodesk" ?selected=${this.newLicense === 'geodesk'}>GeoDesk</option>
                  </select>
                </div>

                <div style="margin-top: 1.75rem; padding-top: 1.25rem; border-top: 1px solid var(--mv-border, #E4E6EF);">
                  <div class="mv-caps" style="margin-bottom: 1rem;">EERSTE BEHEERDER</div>

                  <div class="form-group">
                    <label>E-mailadres</label>
                    <input type="email"
                           .value=${this.newAdminEmail}
                           @input=${e => this.newAdminEmail = e.target.value}
                           placeholder="admin@bedrijf.nl" required />
                  </div>

                  <div class="form-group" style="display:flex; gap:1rem;">
                    <div style="flex:1;">
                      <label>Voornaam</label>
                      <input type="text"
                             .value=${this.newAdminFirstName}
                             @input=${e => this.newAdminFirstName = e.target.value}
                             placeholder="Voornaam" required />
                    </div>
                    <div style="flex:1;">
                      <label>Achternaam</label>
                      <input type="text"
                             .value=${this.newAdminLastName}
                             @input=${e => this.newAdminLastName = e.target.value}
                             placeholder="Achternaam" required />
                    </div>
                  </div>
                </div>

                <div style="display: flex; justify-content: flex-end; gap: 1rem; margin-top: 2rem;">
                  <button type="button" class="btn-secondary" @click=${this.closeCreateModal} ?disabled=${this.isCreating}>Annuleren</button>
                  <button type="submit" class="btn-primary" ?disabled=${this.isCreating}>
                    ${this.isCreating ? 'Wordt aangemaakt...' : 'Aanmaken & Ontgrendelen'}
                  </button>
                </div>
              </form>
            </div>
          `}
        </div>
      </div>

      <!-- Bedrijfsdetail: drie tabbladen — bedrijf, leden, labkoppelingen -->
      ${this.detailCompany ? html`
        <div class="mv-modal-overlay" @click=${(e) => e.target === e.currentTarget && this.closeDetail()}>
          <div class="mv-modal">

            <div class="mv-modal-head">
              <span class="mv-avatar" style="width:42px; height:42px; border-radius:var(--mv-radius-lg); font-size:var(--mv-text-lg);">
                ${this._companyInitials(this.detailCompany.name)}
              </span>
              <div style="flex:1; min-width:0;">
                <div style="display:flex; align-items:center; gap:10px; flex-wrap:wrap;">
                  <h2 class="mv-h2">${this.detailCompany.name}</h2>
                  <span class="mv-pill" style="font-family:ui-monospace,SFMono-Regular,Menlo,monospace; border-radius:var(--mv-radius-xs); letter-spacing:0.04em;">${this.detailCompany.db_binding}</span>
                </div>
                <div style="font-size:var(--mv-text-sm); color:var(--mv-text-sub); margin-top:3px;">
                  ${this._detailSubtitle()}
                </div>
              </div>
              <button class="mv-icon-btn" @click=${this.closeDetail} title="Sluiten">${unsafeSVG(lucideIcon(X, 16))}</button>
            </div>

            <div style="display:flex; gap:4px; padding:10px 26px 0; border-bottom:1px solid var(--mv-border-soft);">
              ${[
                { id: 'company', label: 'Bedrijf', count: null },
                { id: 'members', label: 'Leden', count: this.members?.length ?? null },
                { id: 'labs', label: 'Labkoppelingen', count: this.labConnections?.length ?? null },
                { id: 'ai', label: 'AI-kosten', count: null },
              ].map(tab => html`
                <button @click=${() => { this.detailTab = tab.id; }}
                  style="display:flex; align-items:center; gap:8px; padding:11px 14px; border:none; background:none; cursor:pointer; font-family:inherit; font-size:var(--mv-text-md); font-weight:var(--mv-weight-bold); color:${this.detailTab === tab.id ? 'var(--mv-text)' : 'var(--mv-text-sub)'}; border-bottom:2.5px solid ${this.detailTab === tab.id ? 'var(--mv-primary)' : 'transparent'}; margin-bottom:-1px;">
                  ${tab.label}
                  ${tab.count === null ? '' : html`<span class="mv-pill" style="background:${this.detailTab === tab.id ? 'var(--mv-primary-tint)' : 'var(--mv-surface-alt)'}; color:${this.detailTab === tab.id ? 'var(--mv-primary)' : 'var(--mv-text-sub)'};">${tab.count}</span>`}
                </button>`)}
            </div>

            <div class="mv-modal-body">
              ${this.detailTab === 'ai' ? this._renderAiTab()
                : this.detailTab === 'labs' ? this._renderLabConnections()
                : this.detailTab === 'members' ? this._renderMembersTab()
                : this._renderCompanyTab()}
            </div>
          </div>
        </div>
      ` : ''}
    `;
    }
}

customElements.define('company-dashboard', CompanyDashboard);
